/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Typography2 } from '@coursera/cds-core';

import { getInitialsFromFullName } from 'bundles/common/utils/commonUtils';

import _t from 'i18n!nls/enterprise-ui';

// External image hosts that don't work through Coursera's image proxy
// and should be loaded directly without going through Imgix
const EXTERNAL_IMAGE_HOSTS = ['googleusercontent.com'];

/**
 * Checks if a URL's hostname matches any of the external image hosts that
 * should bypass the Coursera image proxy.
 * Uses URL parsing to ensure we're checking the actual hostname, not just
 * a substring match (which could be spoofed in the path).
 */
const shouldBypassImageProxy = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);
    return EXTERNAL_IMAGE_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`));
  } catch {
    // If URL parsing fails, don't bypass the proxy
    return false;
  }
};

/**
 * @deprecated use CDS Avatar instead
 */
export const Avatar = ({
  style = {},
  imgSrc,
  fullName,
  size = 38,
}: {
  style?: React.CSSProperties;
  imgSrc?: string;
  fullName?: string;
  size?: number;
}) => {
  const getBackgroundColor = () => {
    if (fullName && fullName.length > 0) {
      // Using the last letter so that people with the same first name get different colors
      const colorCode = fullName.charCodeAt(fullName.length - 1) % 3;
      if (colorCode === 0) {
        return 'var(--cds-color-purple-950)';
      } else if (colorCode === 1) {
        return 'var(--cds-color-green-700)';
      } else {
        return 'var(--cds-color-blue-700)';
      }
    }
    return 'var(--cds-color-grey-50)';
  };

  const commonStyles = css({
    color: 'var(--cds-color-neutral-primary-invert)',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    overflow: 'hidden',
    objectFit: 'cover',
  });

  const dynamicStyles: React.CSSProperties = {
    backgroundColor: getBackgroundColor(),
    fontSize: size / 2,
    height: size,
    width: size,
    minWidth: size,
  };
  const mergedStyles: React.CSSProperties = { ...dynamicStyles, ...style };

  if (imgSrc) {
    // Bypass Imgix proxy for external image hosts that don't work through it
    // (e.g., Google profile photos for users who signed in with Google)
    if (shouldBypassImageProxy(imgSrc)) {
      return (
        <img
          alt={fullName ?? _t('Profile photo')}
          css={commonStyles}
          style={mergedStyles}
          src={imgSrc}
          role="presentation"
        />
      );
    }

    return (
      <Imgix
        alt={fullName ?? _t('Profile photo')}
        css={commonStyles}
        style={mergedStyles}
        src={imgSrc}
        role="presentation"
      />
    );
  }
  return (
    <div css={commonStyles} style={mergedStyles}>
      <Typography2 component="h4" variant="subtitleMedium" color="invertBody">
        {getInitialsFromFullName(fullName ?? '')}
      </Typography2>
    </div>
  );
};
